import React, { useState, createContext, useContext, useEffect } from 'react';
import { useRole } from '../context/RoleContext';
import { useChat } from '../context/ChatContext';
import LeftSidebar, { NavigationProvider, useNavigation } from './LeftSidebar';
import RightHistoryPanel from './RightHistoryPanel';
import MisClasesView from './MisClasesView';
import CrearRetosView from './CrearRetosView';
import StudentRetosView from './StudentRetosView';
import ProfesorResultadosView from './ProfesorResultadosView';
import ProfesorResultadosDashboard from './ProfesorResultadosDashboard';
import StudentResultadosView from './StudentResultadosView';
import NotificationsView from './NotificationsView';
import FactCheckView from './FactCheckView';
import CenterHome from './CenterHome';

// Context to share the history panel toggle function
const HistoryPanelContext = createContext();

export const useHistoryPanel = () => {
  const context = useContext(HistoryPanelContext);
  return context;
};

// Inner component that uses navigation context
const AppShellInner = () => {
  const { isProfessor } = useRole();
  const { activeNav, setActiveNav } = useNavigation();
  const { setSelectedSubject, sendMessage, subjects } = useChat();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [openChallengeId, setOpenChallengeId] = useState(null);

  // Listen for navigateToChat events from other components
  useEffect(() => {
    const handleNavigateToChat = (event) => {
      const { subjectId, question } = event.detail || {};
      
      // Find the subject by ID
      if (subjectId && subjects) {
        const targetSubject = subjects.find(s => s.id === subjectId);
        if (targetSubject) {
          setSelectedSubject(targetSubject);
        }
      }
      
      // Navigate to chat (CenterHome)
      setActiveNav('chat');
      
      // Send the message after a short delay to ensure navigation completes
      if (question) {
        setTimeout(() => {
          sendMessage(question);
        }, 500);
      }
    };

    window.addEventListener('navigateToChat', handleNavigateToChat);
    return () => {
      window.removeEventListener('navigateToChat', handleNavigateToChat);
    };
  }, [subjects, setSelectedSubject, sendMessage, setActiveNav]);

  const handleOverlayClick = () => {
    setSidebarOpen(false);
    setHistoryOpen(false);
  };

  const openHistoryPanel = () => setHistoryOpen(true);

  // Handle opening a specific challenge from notifications
  const handleOpenChallenge = (challengeId) => {
    setOpenChallengeId(challengeId);
    setActiveNav('student-retos');
  };

  // Clear the challenge ID after it's been used
  const clearOpenChallengeId = () => {
    setOpenChallengeId(null);
  };

  // Determine if we should show specific views
  const showMisClases = activeNav === 'mis-clases' && isProfessor;
  const showCrearRetos = activeNav === 'crear-retos' && isProfessor;
  const showProfesorResultados = activeNav === 'profesor-resultados' && isProfessor;
  const showFactCheck = activeNav === 'fact-check' && isProfessor;
  const showStudentRetos = activeNav === 'student-retos' && !isProfessor;
  const showStudentResultados = activeNav === 'student-resultados' && !isProfessor;
  const showNotifications = activeNav === 'notifications';

  // Determine which content to show
  const renderContent = () => {
    if (showNotifications) return <NotificationsView onOpenChallenge={handleOpenChallenge} />;
    if (showMisClases) return <MisClasesView />;
    if (showCrearRetos) return <CrearRetosView />;
    if (showProfesorResultados) return <ProfesorResultadosDashboard />;
    if (showFactCheck) return <FactCheckView />;
    if (showStudentRetos) return <StudentRetosView openChallengeId={openChallengeId} onChallengeOpened={clearOpenChallengeId} />;
    if (showStudentResultados) return <StudentResultadosView />;
    return <CenterHome />;
  };

  return (
    <HistoryPanelContext.Provider value={{ openHistoryPanel }}>
      <div 
        className={`app-wrapper ${isProfessor ? 'dark-mode' : ''}`} 
        data-testid="app-wrapper"
      >
        <div 
          className={`app-shell ${isProfessor ? 'app-shell-dark' : ''}`} 
          data-testid="app-shell"
        >
          {/* Mobile Menu Button - Hamburger style */}
          <button
            className={`fixed top-4 left-4 z-[70] md:hidden transition-all duration-200 p-2 rounded-xl ${
              sidebarOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
            } ${isProfessor ? 'bg-gray-800/90 text-white' : 'bg-white/90 text-gray-800'} shadow-lg backdrop-blur-sm`}
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Overlay */}
          <div
            className={`drawer-overlay ${(sidebarOpen || historyOpen) ? 'visible' : ''}`}
            onClick={handleOverlayClick}
            data-testid="drawer-overlay"
          />

          {/* Left Sidebar */}
          <LeftSidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />

          {/* Center Content */}
          <main 
            className={`center-content ${isProfessor ? 'center-content-dark' : ''}`} 
            data-testid="center-content"
          >
            {renderContent()}
          </main>
        </div>

        {/* Right History Panel - Always visible */}
        <RightHistoryPanel
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      </div>
    </HistoryPanelContext.Provider>
  );
};

// Main AppShell component with NavigationProvider
const AppShell = () => {
  return (
    <NavigationProvider>
      <AppShellInner />
    </NavigationProvider>
  );
};

export default AppShell;
